import Button from '@jetbrains/ring-ui/components/button/button'
import React, {useState} from 'react'

import {useJobId} from '../../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {isPipelineFormOpened} from '../../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {pipelineDraft} from '../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {FormType} from '../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices.types'
import {usePipelineId} from '../../../../../../../hooks/pipeline'
import EditSettingsForm from '../../../../../EditSettingsForm/EditSettingsForm'
import Input, {Size} from '../../../../../Input'
import {IntegrationType} from '../../Integrations.consts'
import {useGenerateFreeIntegrationName} from '../../Integrations.hooks'

import {dockerDefaultRepositoryUrl, passwordPlaceholder} from './DockerIntegrationForm.consts'

import styles from './DockerIntegrationForm.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {restApi} from 'src/services/rest'

type Props = {
  id?: string
  parameters?: Record<string, string>
  onClose: () => void
}

function DockerIntegrationForm({id, parameters, onClose}: Props) {
  const jobId = useJobId()
  const pipelineId = usePipelineId()

  const isOpenedExternally = useAppSelector(state =>
    isPipelineFormOpened(state, IntegrationType.DOCKER, FormType.PIPELINE_INTEGRATION),
  )
  const prefilledUsername = useAppSelector(state => {
    const {data} = state.pipelines.pipelineForm
    return isOpenedExternally &&
      data != null &&
      typeof data === 'object' &&
      'username' in data &&
      data.username != null
      ? String(data.username)
      : ''
  })

  const [userNameState, setUserName] = useState(parameters?.userName)
  const userName = userNameState ?? prefilledUsername
  const [url, setUrl] = useState(parameters?.repositoryUrl ?? dockerDefaultRepositoryUrl)

  const isEditMode = id != null && parameters != null
  const hasPassword = isEditMode && parameters['secure:userPass'] !== ''
  const [password, setPassword] = useState(hasPassword ? passwordPlaceholder : '')

  const generateFreeIntegrationName = useGenerateFreeIntegrationName('Docker')
  const dispatch = useAppDispatch()

  const [testDockerConnection, {testResult, isTesting}] =
    restApi.endpoints.testDockerConnection.useMutation({
      selectFromResult: ({data, isLoading}) => ({
        testResult: data,
        isTesting: isLoading,
      }),
    })
  const errors = [...(testResult?.querySelectorAll('error') ?? [])]

  const isButtonTestConnectionDisabled = !userName || !password

  const confirm = () => {
    const newParameters: Record<string, string> = {
      repositoryUrl: url,
      userName,
    }
    if (!hasPassword || password !== passwordPlaceholder) {
      newParameters['secure:userPass'] = password
    }

    dispatch(
      isEditMode
        ? pipelineDraft.actions.editIntegration({
            pipelineId,
            id,
            parameters: newParameters,
          })
        : pipelineDraft.actions.addIntegration({
            pipelineId,
            jobId,
            type: IntegrationType.DOCKER,
            parameters: {
              ...newParameters,
              displayName: generateFreeIntegrationName(),
            },
          }),
    )

    onClose()
  }

  const deleteHandler = () => {
    dispatch(
      pipelineDraft.actions.deleteIntegration({
        id: id!,
        name: parameters!.displayName!,
        pipelineId,
      }),
    )

    onClose()
  }

  return (
    <EditSettingsForm
      className={styles.dockerIntegrationForm}
      onConfirmClick={confirm}
      onCancelClick={onClose}
      onDeleteClick={isEditMode ? deleteHandler : undefined}
    >
      <Input
        label={'URL'}
        type="url"
        spellCheck={false}
        size={Size.FULL}
        value={url}
        onChange={e => setUrl(e.currentTarget.value)}
        autoFocus
      />
      <Input
        size={Size.FULL}
        label={'Username'}
        value={userName}
        onChange={e => setUserName(e.currentTarget.value)}
      />
      <Input
        label={'Password'}
        size={Size.FULL}
        data-imitate-password
        placeholder={'Leave empty for anonymous'}
        value={password}
        onChange={e => setPassword(e.currentTarget.value)}
      />
      <div className={styles.testDockerConnectionBlock}>
        <Button
          className={styles.testDockerConnection}
          onClick={() => testDockerConnection({url, userName, userPass: password})}
          loader={isTesting}
          disabled={isButtonTestConnectionDisabled}
          text
        >
          {'Test Connection'}
        </Button>
        {testResult != null &&
          (errors.length > 0 ? (
            errors.map(error => (
              <span key={error.textContent} className={styles.error}>
                {error.textContent}
              </span>
            ))
          ) : (
            <span className={styles.success}>{'Success'}</span>
          ))}
      </div>
    </EditSettingsForm>
  )
}

export default DockerIntegrationForm
